import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import styles from "./Pagination.module.css";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  pageSize: number;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
}

function getPageNumbers(currentPage: number, totalPages: number): (number | "ellipsis")[] {
  const pages: (number | "ellipsis")[] = [];
  
  if (totalPages <= 7) {
    // Show all pages if 7 or less
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(1);
    
    if (currentPage <= 3) {
      // Near the start: 1 2 3 4 5 ... last
      pages.push(2, 3, 4, 5);
      pages.push("ellipsis");
      pages.push(totalPages);
    } else if (currentPage >= totalPages - 2) {
      // Near the end: 1 ... last-4 last-3 last-2 last-1 last
      pages.push("ellipsis");
      for (let i = totalPages - 4; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // In the middle: 1 ... current-1 current current+1 ... last
      pages.push("ellipsis");
      pages.push(currentPage - 1, currentPage, currentPage + 1);
      pages.push("ellipsis");
      pages.push(totalPages);
    }
  }
  
  return pages;
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  totalItems,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  const pageNumbers = getPageNumbers(currentPage, totalPages);
  
  if (totalPages <= 1 && totalItems <= pageSize) {
    // Don't show pagination if only one page and few items
    return (
      <div className={styles.pagination}>
        <span className={styles.paginationInfo}>
          Mostrando {startItem}–{endItem} de {totalItems}
        </span>
      </div>
    );
  }
  
  return (
    <div className={styles.pagination}>
      <span className={styles.paginationInfo}>
        Mostrando {startItem}–{endItem} de {totalItems}
      </span>
      
      <div className={styles.pageButtons}>
        <button
          className={`${styles.pageButton} ${styles.navButton}`}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          aria-label="Página anterior"
        >
          <ChevronLeft size={16} />
          Anterior
        </button>
        
        {pageNumbers.map((page, index) => 
          page === "ellipsis" ? (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>
              ...
            </span>
          ) : (
            <button
              key={page}
              className={`${styles.pageButton} ${currentPage === page ? styles.pageButtonActive : ""}`}
              onClick={() => onPageChange(page)}
              aria-label={`Página ${page}`}
              aria-current={currentPage === page ? "page" : undefined}
            >
              {page}
            </button>
          )
        )}
        
        <button
          className={`${styles.pageButton} ${styles.navButton}`}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          aria-label="Próxima página"
        >
          Próximo
          <ChevronRight size={16} />
        </button>
      </div>
      
      {onPageSizeChange && (
        <div className={styles.pageSizeSelector}>
          <span className={styles.pageSizeLabel}>Por página:</span>
          <select
            className={styles.pageSizeSelect}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
